<template>
  <RcModal
    :id="modalId"
    size="md"
    contentClass="rc-modal-form"
    hideFooter
    @hide="onHide"
  >
    <template #modal-title>
      {{ modalTitle }}
    </template>

    <template #modal-body="{ ok, cancel }">
      <!-- add screener to playlist -->
      <template v-if="modal.mode == 'add'">
        <!-- add to existing playlist -->
        <template v-if="showPlaylistOptions">
          <label
            :for="playlistInputOptions.id"
            class="form-label"
          >{{
            $t("catalog.playlist_select")
          }}</label>

          <RcComboBox
            v-bind="playlistInputOptions"
            :apiListOptions="playlistApiOptions"
            size="lg"
            @selected="onPlaylistSelected"
            @init="onInitPlaylists"
          />

          <div
            v-if="validateState('uid') == false"
            id="feedback_invalid__playlistUid"
            aria-live="assertive"
            aria-atomic="true"
            class="invalid-feedback d-block"
          >
            {{ $t("catalog.playlist_required") }}
          </div>
          <div class="my-4">
            <div class="d-flex justify-content-center">
              <RcDebounced
                variant="primary"
                block
                size="lg"
                :promise="addToPlaylist.bind(ok, cancel)"
              >
                {{ $t("common.save") }}
              </RcDebounced>
            </div>
            <div class="d-flex justify-content-center">
              <rc-btn
                v-orphan
                variant="link"
                block
                class="rc-btn-link-secondary mt-3 btn-sm fw-normal"
                @click="changeModalMode"
              >
                {{ $t("catalog.playlist_add_new") }}
              </rc-btn>
            </div>
          </div>
        </template>

        <!-- add to new playlist -->
        <template v-else>
          <p class="mt-1">
            {{ $t("catalog.playlist_name_message") }}
          </p>

          <label
            for="playlistInput"
            class="form-label"
          >{{
            $t("catalog.playlist_title")
          }}</label>
          <input
            id="playlistInput"
            v-model="form.name"
            type="text"
            class="form-control form-control-lg"
            :class="validateState('name') != false ? '' : 'is-invalid'"
            :aria-invalid="validateState('name') != false ? null : true"
            :aria-describedby="
              validateState('name') != false
                ? null
                : 'feedback_invalid__playlistName'
            "
          >

          <div
            v-if="validateState('name') == false"
            id="feedback_invalid__playlistName"
            aria-live="assertive"
            aria-atomic="true"
            class="invalid-feedback d-block"
          >
            <div v-if="!v$.form.name.required.$response">
              {{ $t("catalog.title_required") }}
            </div>
            <div v-if="!v$.form.name.maxLength.$response">
              {{ $t("catalog.title_too_long") }}
              ({{ v$.form.name.maxLength.$params.max }})
            </div>
          </div>
          <div class="my-4">
            <div class="d-flex justify-content-center">
              <RcDebounced
                variant="primary"
                block
                size="lg"
                :promise="handleOk.bind(ok, cancel)"
              >
                {{ $t("common.save") }}
              </RcDebounced>
            </div>
          </div>
        </template>
      </template>

      <!-- create a playlist -->
      <template v-if="modal.mode == 'create'">
        <label
          for="playlistInput"
          class="form-label"
        >{{
          $t("catalog.playlist_title")
        }}</label>

        <input
          id="playlistInput"
          v-model="form.name"
          type="text"
          class="form-control form-control-lg"
          :class="validateState('name') != false ? '' : 'is-invalid'"
          :aria-invalid="validateState('name') != false ? null : true"
          :aria-describedby="
            validateState('name') != false
              ? null
              : 'feedback_invalid__playlistName'
          "
        >

        <div
          v-if="validateState('name') == false"
          id="feedback_invalid__playlistName"
          aria-live="assertive"
          aria-atomic="true"
          class="invalid-feedback d-block"
        >
          <div v-if="!v$.form.name.required.$response">
            {{ $t("catalog.title_required") }}
          </div>
          <div v-if="!v$.form.name.maxLength.$response">
            {{ $t("catalog.title_too_long") }}
            ({{ v$.form.name.maxLength.$params.max }})
          </div>
        </div>
        <div class="my-4">
          <div class="d-flex justify-content-center">
            <RcDebounced
              variant="primary"
              block
              size="lg"
              :promise="handleOk.bind(ok, cancel)"
            >
              {{ $t("common.save") }}
            </RcDebounced>
          </div>
          <div class="d-flex justify-content-center">
            <rc-btn
              v-orphan
              variant="link"
              block
              class="rc-btn-link-secondary mt-3 btn-sm fw-normal"
              @click="changeModalMode"
            >
              {{ $t("catalog.playlist_existing_select") }}
            </rc-btn>
          </div>
        </div>
      </template>
    </template>
  </RcModal>
</template>

<script>
import { mapState } from 'vuex';

import RcDebounced from '@/libvue/components/button/DebouncedButton.vue';
import { playlistsMixin } from '@/libvue/apps/catalog/playlists/mixins';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import RcModal from '@/libvue/components/modal/Modal.vue';
import RcComboBox from '@/libvue/components/select/comboBox.vue';

export default {
  name: 'RcCreatePlaylistModal',

  components: {
    RcDebounced,
    RcModal,
    RcComboBox
  },

  mixins: [playlistsMixin],

  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      modalId: 'createPlaylistModal',
      showPlaylistOptions: true,
      form: {
        name: null,
        uid: null
      }
    };
  },

  validations: {
    form: {
      name: { required, maxLength: maxLength(200) },
      uid: { required }
    }
  },

  computed: {
    ...mapState({
      profile: (state) => state.auth.user
    }),

    modalTitle() {
      return this.modal.item
        ? this.$t('catalog.playlist_add_to')
        : this.$t('catalog.create_playlist');
    },
    playlistApiOptions() {
      return {
        url: `users/${this.profile.user_uid}/playlists`,
        method: 'get',
        params: {
          playlist_type: this.form.playlist_type,
          ordering: 'name'
        }
      };
    },
    playlistInputOptions() {
      return {
        id: 'playlistSelect',
        placeholder: this.$t('catalog.playlist_search'),
        class: this.validateState('uid') != false ? '' : 'is-invalid',
        'aria-invalid': this.validateState('uid') != false ? null : true,
        'aria-describedby':
          this.validateState('uid') != false
            ? null
            : 'feedback_invalid__playlistUid'
      };
    }
  },

  methods: {
    validateState(key) {
      const { $dirty, $error } = this.v$.form[key];
      return $dirty ? !$error : null;
    },
    handleOk(ok) {
      this.v$.form.$touch();

      if (this.v$.form.name.$error) {
        return;
      }

      if (this.modal.item) {
        const payload = {
          playlist_name: this.form.name,
          screener: this.modal?.item?.screeners[0]?.uid,
          position: 0
        };

        this.addScreeners(payload, this.form.name).then(() => ok());
      } else {
        this.createPlaylist({
          screeners_ids: [],
          name: this.form.name
        }).then(() => ok());
      }
    },
    addToPlaylist(ok) {
      this.v$.form.$touch();

      if (this.v$.form.uid.$error) {
        return;
      }
      if (!this.addToPlaylistUid) return;

      const payload = {
        playlist_id: this.addToPlaylistUid,
        screener: this.modal.item?.screeners[0].uid,
        position: 0
      };

      this.addScreeners(payload, this.form.name).then(() => ok());
    },
    onPlaylistSelected(selected) {
      if (selected) {
        this.addToPlaylistUid = selected.uid;
        this.form.uid = selected.uid;
        this.form.name = selected.name;
      } else {
        this.addToPlaylistUid = null;
        this.form.uid = null;
        this.form.name = null;
      }
      this.validateState('uid');
    },
    onInitPlaylists(e) {
      this.showPlaylistOptions = Boolean(e.length);
    },
    onHide(e) {
      this.v$.form.$reset();
    }
  }
};
</script>
